// EM²c on-site construction integration.
// Construction Yard/module assembly has been removed. Drones/rovers build the
// selected structure directly on its final hex footprint.
//
// Locked recycling rules:
// - cancel/demolish unfinished construction => 100% of paid construction resources returned
// - demolish completed building => age-based Recyclable Material, never a direct resource refund
// - Recyclable Material is processed by the Waste Recycling Plant

import { useGame } from "../em2c-store";
import { axialToWorld } from "../hexgrid";
import { occupyHexes, freeHexes } from "../hexplacement";
import { isBuildingUnlocked } from "../data/research";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { getDefaultRecipeIdForType, getPreferredBuildPayment } from "../data/industrialChainRules";
import { sfxClick, sfxError } from "../audio";

const uid = () => Math.random().toString(36).slice(2, 10);
let installed = false;
let baseLoad = null;
let baseReset = null;

function seedDefaultRecipes() {
  const s = useGame.getState();
  const next = { ...(s.selectedRecipes || {}) };
  let changed = false;
  for (const building of s.buildings || []) {
    if (next[building.id]) continue;
    const recipeId = getDefaultRecipeIdForType(building.type);
    if (!recipeId) continue;
    next[building.id] = recipeId;
    changed = true;
  }
  if (changed) useGame.setState({ selectedRecipes: next });
  return changed;
}

export function repairBootstrapChainState() {
  seedDefaultRecipes();
}

function returnConstructionResources(resources, cost) {
  const next = { ...resources };
  for (const [key, amount] of Object.entries(cost || {})) {
    const current = next[key];
    if (!current) continue;
    const stored = Number(current.stored || 0) + Number(amount || 0);
    next[key] = { ...current, stored, max: Math.max(Number(current.max || 0), stored) };
  }
  return next;
}

export function getAgeSalvageRatio(ageSols) {
  // New structures yield more reusable material; old structures yield less.
  return Math.max(0.25, 0.85 - Math.max(0, Number(ageSols || 0)) * 0.015);
}

function salvageAmountFor(building, def, currentSol) {
  const paid = building.constructionPayment?.cost || def?.cost || {};
  const rawMaterialValue = Object.values(paid).reduce((sum, amount) => sum + Math.max(0, Number(amount || 0)), 0);
  const age = Math.max(0, Number(currentSol || 1) - Number(building.constructionStartedSol || building.builtSol || currentSol || 1));
  return Math.max(1, Math.floor(rawMaterialValue * getAgeSalvageRatio(age)));
}

export function installBootstrapBuildIntegration() {
  if (installed) {
    repairBootstrapChainState();
    return;
  }
  installed = true;

  const initial = useGame.getState();
  baseLoad = initial.load;
  baseReset = initial.reset;

  useGame.setState({
    getLocalModuleCost: () => null,

    getBuildPayment: (type) => {
      const s = useGame.getState();
      return getPreferredBuildPayment(type, EM2C_BUILDINGS[type], s.resources);
    },

    canAfford: (type) => {
      const s = useGame.getState();
      return !!getPreferredBuildPayment(type, EM2C_BUILDINGS[type], s.resources);
    },

    placeBuilding: (q, r) => {
      const s = useGame.getState();
      const type = s.placementType;
      if (!type) return;
      const def = EM2C_BUILDINGS[type];
      if (!def || !isBuildingUnlocked(def, s.research?.completed || [])) return;

      const payment = getPreferredBuildPayment(type, def, s.resources);
      if (!payment) {
        s.pushNotification?.({ level: "warning", text: `Not enough resources for ${def.name}` });
        sfxError();
        return;
      }

      const result = s.isValidHexPlacement(type, q, r, s.placementRotation);
      if (!result.valid) {
        s.pushNotification?.({ level: "warning", text: result.reason });
        sfxError();
        return;
      }

      const resources = { ...s.resources };
      for (const [key, amount] of Object.entries(payment.cost || {})) {
        const current = resources[key];
        if (!current || Number(current.stored || 0) + 1e-9 < amount) {
          s.pushNotification?.({ level: "warning", text: `Missing ${key.replace(/_/g, " ")}` });
          sfxError();
          return;
        }
        resources[key] = { ...current, stored: current.stored - amount };
      }

      const worldPos = axialToWorld(q, r);
      const id = uid();
      const newBuilding = {
        id,
        type,
        q,
        r,
        rotation: s.placementRotation,
        condition: 1,
        active: true,
        progress: 0,
        workers: 0,
        state: "under_construction",
        x: worldPos.x,
        z: worldPos.z,
        constructionStartedSol: s.sol,
        constructionPayment: { source: "on_site_materials", cost: { ...(payment.cost || {}) } },
      };

      occupyHexes(s.grid, def.footprintHexes, q, r, s.placementRotation, id);
      const selectedRecipes = { ...(s.selectedRecipes || {}) };
      const defaultRecipe = getDefaultRecipeIdForType(type);
      if (defaultRecipe) selectedRecipes[id] = defaultRecipe;

      useGame.setState({ buildings: [...s.buildings, newBuilding], resources, selectedRecipes });
      sfxClick();
      s.pushNotification?.({ level: "info", text: `${def.name} construction started on site` });
    },

    demolish: (id) => {
      const s = useGame.getState();
      const building = s.buildings.find((item) => item.id === id);
      if (!building) return false;
      const def = EM2C_BUILDINGS[building.type];
      if (!def) return false;

      let resources = { ...s.resources };
      const unfinished = Number(building.progress || 0) < 1;

      if (unfinished) {
        resources = returnConstructionResources(resources, building.constructionPayment?.cost || def.cost || {});
      } else {
        const salvage = salvageAmountFor(building, def, s.sol);
        const current = resources.recyclable_material || { stored: 0, max: 500 };
        const room = Math.max(0, Number(current.max || 500) - Number(current.stored || 0));
        if (room + 1e-9 < salvage) {
          s.pushNotification?.({ level: "warning", text: `Need ${salvage} free Recyclable Material storage before demolition` });
          sfxError();
          return false;
        }
        resources.recyclable_material = { ...current, stored: Number(current.stored || 0) + salvage };
      }

      if (def.footprintHexes) freeHexes(s.grid, def.footprintHexes, building.q, building.r, building.rotation);

      const selectedRecipes = { ...(s.selectedRecipes || {}) };
      delete selectedRecipes[id];
      useGame.setState({
        buildings: s.buildings.filter((item) => item.id !== id),
        resources,
        selectedRecipes,
        selectedId: s.selectedId === id ? null : s.selectedId,
      });

      sfxClick();
      s.pushNotification?.({
        level: "info",
        text: unfinished
          ? `${def.name} construction cancelled — 100% resources returned`
          : `${def.name} demolished — salvage moved to Recyclable Material`,
      });
      return true;
    },

    load: () => {
      baseLoad?.();
      seedDefaultRecipes();
    },

    reset: () => {
      baseReset?.();
      seedDefaultRecipes();
    },
  });

  seedDefaultRecipes();
}
